import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  InputAdornment,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SearchIcon from '@mui/icons-material/Search';
import { DataGrid } from '@mui/x-data-grid';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import Header from '../../../components/Header';
import config from '../../../config';
import { tokens } from '../../../theme';
import { getAuthToken } from '../../../utils/authStorage';
import { HPC_COLORS } from '../hpcTheme';
import { viewProfileButtonSx } from '../hpcAdminStatusStyles';
import {
  ASSESSMENT_STATUS_TABS,
  formatMobilityClass,
} from './assessmentFormConfig';
import {
  formatBmiCategory,
  formatWhtrCategory,
} from './healthCalculations';
import {
  assessmentFilterToApi,
  formatStatusLabel,
  getAssessmentStatusChipSx,
  getFilterToggleSx,
} from './assessmentStatusStyles';
import { downloadAssessmentPdf } from './assessmentExport';
import DeleteAssessmentDialog, { deleteButtonSx } from './DeleteAssessmentDialog';

const authHeaders = () => {
  const token = getAuthToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const pdfButtonSx = {
  color: '#ffffff',
  borderColor: HPC_COLORS.tebaBlue,
  backgroundColor: 'rgba(43, 124, 211, 0.28)',
  borderWidth: 2,
  fontWeight: 700,
  textTransform: 'none',
  '&:hover': {
    borderColor: '#5a9ae8',
    backgroundColor: 'rgba(43, 124, 211, 0.45)',
    color: '#ffffff',
    borderWidth: 2,
  },
  '&.Mui-disabled': {
    color: 'rgba(255,255,255,0.45)',
    borderColor: 'rgba(43, 124, 211, 0.35)',
    backgroundColor: 'rgba(43, 124, 211, 0.12)',
  },
};

const reviewButtonSx = {
  bgcolor: '#2e7c67',
  color: '#ffffff',
  fontWeight: 700,
  textTransform: 'none',
  '&:hover': { bgcolor: '#3a9a80' },
  '&.Mui-disabled': {
    color: 'rgba(255,255,255,0.45)',
    bgcolor: 'rgba(46, 124, 103, 0.35)',
  },
};

const AssessmentList = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'lg'));
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const [assessments, setAssessments] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const [exporting, setExporting] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const gridMinWidth = isMobile ? 1100 : isTablet ? 980 : 0;

  const selectedRow = useMemo(
    () => assessments.find((row) => row.id === selectedId) || null,
    [assessments, selectedId]
  );

  const fetchAssessments = useCallback(async () => {
    setLoading(true);
    try {
      const apiStatus = assessmentFilterToApi(statusFilter);
      const params = {};
      if (apiStatus) params.status = apiStatus;
      if (searchQuery) params.q = searchQuery;
      const response = await axios.get(`${config.apiBaseUrl}/hpc/admin/assessments`, {
        params,
        headers: authHeaders(),
      });
      setAssessments(response.data.assessments || []);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to load assessments');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchQuery]);

  const handleDeleteConfirm = async () => {
    if (!selectedRow) return;
    setDeleting(true);
    try {
      await axios.delete(
        `${config.apiBaseUrl}/hpc/admin/assessments/${selectedRow.id}`,
        { headers: authHeaders() }
      );
      toast.success('Assessment deleted');
      setDeleteOpen(false);
      setSelectedId(null);
      fetchAssessments();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete assessment');
    } finally {
      setDeleting(false);
    }
  };

  const handleExportPdf = async () => {
    if (!selectedRow) return;
    setExporting(true);
    try {
      await downloadAssessmentPdf(selectedRow.id, {
        membershipId: selectedRow.membership_id,
        assessmentDate: selectedRow.assessment_date,
      });
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  const handleMarkReviewed = async () => {
    if (!selectedRow || selectedRow.status !== 'completed') return;
    setReviewing(true);
    try {
      await axios.post(
        `${config.apiBaseUrl}/hpc/admin/assessments/${selectedRow.id}/review`,
        {},
        { headers: authHeaders() }
      );
      toast.success('Assessment marked as reviewed');
      fetchAssessments();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Could not mark assessment as reviewed');
    } finally {
      setReviewing(false);
    }
  };

  useEffect(() => {
    fetchAssessments();
  }, [fetchAssessments]);

  useEffect(() => {
    const timer = setTimeout(() => setSearchQuery(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setSelectedId(null);
  }, [statusFilter, searchQuery]);

  useEffect(() => {
    if (selectedId && !assessments.some((row) => row.id === selectedId)) {
      setSelectedId(null);
    }
  }, [assessments, selectedId]);

  const columns = [
    { field: 'membership_id', headerName: 'Membership ID', flex: 1, minWidth: 150 },
    { field: 'member_name', headerName: 'Member', flex: 1.2, minWidth: 180 },
    { field: 'assessment_date', headerName: 'Date', flex: 0.8, minWidth: 120 },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.8,
      minWidth: 130,
      renderCell: ({ value }) => (
        <Chip label={formatStatusLabel(value)} size="small" sx={getAssessmentStatusChipSx(value)} />
      ),
    },
    { field: 'bmi', headerName: 'BMI', flex: 0.5, minWidth: 90 },
    {
      field: 'bmi_category',
      headerName: 'BMI Status',
      flex: 1,
      minWidth: 140,
      valueFormatter: (value) => formatBmiCategory(value),
    },
    {
      field: 'whtr_category',
      headerName: 'WHtR Status',
      flex: 1.1,
      minWidth: 150,
      valueFormatter: (value) => formatWhtrCategory(value),
    },
    { field: 'fms_total', headerName: 'FMS', flex: 0.5, minWidth: 90 },
    { field: 'mobility_total', headerName: 'Mobility', flex: 0.6, minWidth: 110 },
    {
      field: 'mobility_classification',
      headerName: 'Mobility Class',
      flex: 0.9,
      minWidth: 130,
      valueFormatter: (value) => formatMobilityClass(value),
    },
  ];

  return (
    <Box m={{ xs: '12px', sm: '16px', md: '20px' }}>
      <ToastContainer position="top-center" theme="dark" />
      <DeleteAssessmentDialog
        open={deleteOpen}
        target={selectedRow}
        deleting={deleting}
        onCancel={() => !deleting && setDeleteOpen(false)}
        onConfirm={handleDeleteConfirm}
      />
      <Box display="flex" justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} flexWrap="wrap" gap={2}>
        <Header title="HPC Assessments" subtitle="Initial screening & assessment reports" />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/hpc/assessments/new')}
          fullWidth={isMobile}
          sx={{
            ...viewProfileButtonSx,
            px: 2.5,
            py: 1,
            fontSize: '0.95rem',
          }}
        >
          New Assessment
        </Button>
      </Box>

      <Box mt={2} mb={2} display="flex" flexDirection="column" gap={2}>
        <TextField
          size="small"
          placeholder="Search by member name, membership ID, or email"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          sx={{
            width: '100%',
            maxWidth: { xs: '100%', sm: 420 },
            '& .MuiOutlinedInput-root': {
              bgcolor: 'rgba(0,0,0,0.2)',
              color: HPC_COLORS.white,
              '& fieldset': { borderColor: 'rgba(43, 124, 211, 0.45)' },
              '&:hover fieldset': { borderColor: HPC_COLORS.tebaBlue },
              '&.Mui-focused fieldset': {
                borderColor: HPC_COLORS.hpuOrange,
                borderWidth: 2,
              },
            },
            '& .MuiInputLabel-root': { color: HPC_COLORS.silver },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: HPC_COLORS.silver }} />
              </InputAdornment>
            ),
          }}
        />
        <ToggleButtonGroup
          exclusive
          value={statusFilter}
          onChange={(_, value) => value && setStatusFilter(value)}
          size="small"
          aria-label="Filter by assessment status"
          sx={{
            flexWrap: 'wrap',
            gap: 1.5,
            width: '100%',
            '& .MuiToggleButtonGroup-grouped': {
              border: 0,
              borderRadius: '999px !important',
              mx: 0,
              px: { xs: 1.5, sm: 2.5 },
              py: 0.75,
              textTransform: 'none',
              fontSize: { xs: '0.8rem', sm: '0.9rem' },
            },
          }}
        >
          {ASSESSMENT_STATUS_TABS.map((tab) => (
            <ToggleButton key={tab.value} value={tab.value} sx={getFilterToggleSx(tab.accent)}>
              {tab.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {selectedRow && (
        <Box
          display="flex"
          alignItems="center"
          flexWrap="wrap"
          gap={1.5}
          mb={2}
          p={1.5}
          sx={{
            borderRadius: 2,
            bgcolor: 'rgba(0,0,0,0.25)',
            border: `1px solid rgba(245, 166, 35, 0.35)`,
          }}
        >
          <Typography sx={{ color: HPC_COLORS.silver, mr: 1 }}>
            Selected:{' '}
            <strong style={{ color: HPC_COLORS.white }}>
              {selectedRow.member_name || selectedRow.membership_id}
            </strong>
            {' · '}
            {selectedRow.assessment_date}
            {' · '}
            {formatStatusLabel(selectedRow.status)}
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate(`/hpc/assessments/${selectedRow.id}`)}
            sx={viewProfileButtonSx}
            fullWidth={isMobile}
          >
            Open
          </Button>
          <Button
            variant="contained"
            disabled={selectedRow.status !== 'completed' || reviewing}
            onClick={handleMarkReviewed}
            sx={reviewButtonSx}
            fullWidth={isMobile}
          >
            Mark Reviewed
          </Button>
          <Button
            variant="outlined"
            startIcon={<PictureAsPdfIcon />}
            disabled={exporting}
            onClick={handleExportPdf}
            sx={pdfButtonSx}
            fullWidth={isMobile}
          >
            PDF
          </Button>
          <Button
            variant="contained"
            startIcon={<DeleteOutlineIcon />}
            disabled={deleting}
            onClick={() => setDeleteOpen(true)}
            sx={deleteButtonSx}
            fullWidth={isMobile}
          >
            Delete
          </Button>
        </Box>
      )}

      <Box
        height={{ xs: '68vh', sm: '72vh', md: '75vh' }}
        sx={{ overflowX: 'auto' }}
      >
        <Box
          sx={{
            minWidth: gridMinWidth || '100%',
            height: '100%',
            '& .MuiDataGrid-root': { border: 'none' },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: colors.blueAccent[700],
              borderBottom: 'none',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              whiteSpace: 'normal',
              lineHeight: 1.2,
            },
            '& .MuiDataGrid-virtualScroller': { backgroundColor: colors.primary[400] },
            '& .MuiDataGrid-footerContainer': { borderTop: 'none', backgroundColor: colors.blueAccent[700] },
            '& .MuiDataGrid-cell': {
              display: 'flex',
              alignItems: 'center',
              whiteSpace: 'nowrap',
            },
            '& .MuiCheckbox-root': {
              color: `${colors.greenAccent[200]} !important`,
            },
          }}
        >
          <DataGrid
            rows={assessments}
            columns={columns}
            getRowId={(row) => row.id}
            loading={loading}
            checkboxSelection
            disableMultipleRowSelection
            rowSelectionModel={selectedId ? [selectedId] : []}
            onRowSelectionModelChange={(ids) => {
              setSelectedId(ids.length ? ids[0] : null);
            }}
            disableRowSelectionOnClick={false}
            columnBuffer={4}
            rowHeight={isMobile ? 48 : 52}
            columnHeaderHeight={isMobile ? 56 : 52}
            density={isMobile ? 'compact' : 'standard'}
            sx={{ minWidth: gridMinWidth || '100%' }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default AssessmentList;
